import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import AnimatedCounter from "../components/AnimatedCounter";
import {
  Ship,
  Users,
  Database,
  ArrowRight,
} from "lucide-react";
import Seo from "../components/Seo";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "swiper/css/effect-fade";

import foto from "../img/baruu3.webp";
import foto2 from "../img/baru.webp";
import foto3 from "../img/crew2.webp";

const Home = () => {
  const [hasAnimated, setHasAnimated] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    const currentRef = statsRef.current; // <--- Variabel lokal untuk aman saat cleanup

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setHasAnimated(true);
        }
      },
      { threshold: 0.3 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef); // <--- Gunakan variabel lokal di sini
      }
    };
  }, []);

  const stats = [
    {
      icon: Users,
      number: 5000,
      suffix: "+",
      label: "Crews Deployed",
      color: "text-blue-600 bg-blue-50",
    },
    {
      icon: Database,
      number: 7000,
      suffix: "+",
      label: "Talent Pool",
      color: "text-cyan-600 bg-cyan-50",
    },
    {
      icon: Ship,
      number: 5000,
      suffix: "+",
      label: "Vessels Served",
      color: "text-indigo-600 bg-indigo-50",
    },
  ];

  const sliderData = [
    {
      src: foto,
      alt: "PT Kamandanu Jaya Samudera - Professional Indonesian Crew Manning Agency",
    },
    {
      src: foto2,
      alt: "STCW Certified Seafarers and Seamen Training Center Indonesia",
    },
    {
      src: foto3,
      alt: "Global Maritime Manning Services and Crew Management Solutions",
    },
  ];

  // Schema Markup Lengkap untuk SEO Google
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "EducationalOrBusinessOrganization",
    name: "PT Kamandanu Jaya Samudera",
    legalName: "PT Kamandanu Jaya Samudera",
    url: "https://www.ptkamandanujayasamudera.com",
    logo: "https://www.ptkamandanujayasamudera.com/logo.png",
    description:
      "Official Crew Manning Agency in Indonesia providing STCW-certified Indonesian seafarers for international vessels and shipping fleets.",
    address: {
      "@type": "PostalAddress",
      addressCountry: "ID",
    },
    knowsAbout: [
      "Crew Manning Agency",
      "Seafarer Recruitment",
      "Maritime Crew Management",
      "STCW Certification",
    ],
  };

  return (
    <>
      <Seo
        title="PT Kamandanu Jaya Samudera | Official Crew Manning Agency Indonesia"
        description="Leading Indonesian crew manning agency providing STCW-certified seafarers, vessel crew management, and maritime recruitment solutions worldwide."
        url="/"
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>

      <main
        className="text-[#0A2540] bg-white antialiased"
        style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
      >
        {/* ─── HERO SECTION ─── */}
        <header className="relative min-h-[90vh] flex items-center overflow-hidden pt-36 sm:pt-40 lg:pt-48 pb-24">
          <div className="absolute inset-0 z-0 bg-[#071829]">
            <iframe
              className="absolute inset-0 w-full h-full object-cover scale-110 opacity-40 pointer-events-none"
              src="https://www.youtube.com/embed/b_q0Tb2y_qI?autoplay=1&mute=1&loop=1&playlist=b_q0Tb2y_qI&controls=0&rel=0"
              title="PT Kamandanu Jaya Samudera Maritime Operations Video"
              loading="lazy"
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540]/95 via-[#0A2540]/70 to-transparent" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
            <div className="max-w-2xl text-left">
              <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black text-white leading-[1.1] mb-6 tracking-tight">
                Reliable{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">
                  Maritime
                </span>{" "}
                Crewing Excellence.
              </h1>

              <p className="text-lg sm:text-xl text-blue-100/90 mb-10 leading-relaxed">
                Empowering global shipping fleets with Indonesia's finest,
                STCW-certified seafarers and comprehensive crew management
                solutions.
              </p>

              <div className="flex flex-wrap gap-4 items-center">
                <Link
                  to="/about"
                  className="px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-2xl transition-all shadow-lg hover:shadow-blue-500/30 hover:-translate-y-0.5"
                >
                  Company Profile
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* ─── STATISTIK ─── */}
        <section
          ref={statsRef}
          aria-label="Company Statistics"
          className="relative z-20 max-w-6xl mx-auto px-6 -mt-16 sm:-mt-20"
        >
          <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 p-6 sm:p-10 grid grid-cols-1 md:grid-cols-3 gap-8 justify-items-center items-center">
            {stats.map((stat, i) => (
              <div
                key={i}
                className="flex flex-col sm:flex-row items-center justify-center text-center sm:text-left gap-4 w-full"
              >
                <div
                  className={`p-3.5 rounded-2xl ${stat.color} shrink-0 shadow-sm flex items-center justify-center`}
                >
                  <stat.icon size={28} />
                </div>
                <div className="flex flex-col items-center sm:items-start">
                  <div className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-1">
                    {hasAnimated ? (
                      <AnimatedCounter
                        value={stat.number}
                        id={`hero-${i}`}
                        duration={30000}
                      />
                    ) : (
                      <span>0</span>
                    )}
                    <span className="text-blue-600">{stat.suffix}</span>
                  </div>
                  <p className="text-xs sm:text-sm font-bold text-slate-500 uppercase tracking-wider">
                    {stat.label}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ─── ABOUT SECTION ─── */}
        <section className="relative py-20 sm:py-28 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Carousel Gambar */}
            <div className="rounded-[2.5rem] shadow-xl overflow-hidden h-[380px] sm:h-[450px] bg-slate-200 border border-slate-200/80">
              <Swiper
                modules={[Autoplay, Pagination, Navigation, EffectFade]}
                effect="fade"
                autoplay={{ delay: 4500, disableOnInteraction: false }}
                pagination={{ clickable: true }}
                loop
                className="h-full w-full"
              >
                {sliderData.map((img, index) => (
                  <SwiperSlide key={index}>
                    <img
                      src={img.src}
                      alt={img.alt}
                      width="600"
                      height="450"
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            {/* Konten Perusahaan */}
            <article className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-blue-100 text-blue-800 text-xs font-extrabold tracking-widest uppercase">
                Company Profile
              </div>

              <h2 className="text-3xl sm:text-4xl font-black text-[#0A2540] tracking-tight leading-tight">
                PT KAMANDANU JAYA SAMUDERA
              </h2>

              <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                A professional crew manning company in Indonesia specializing in
                providing qualified and competent seafarers for international
                shipping fleets.
              </p>

              <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                Supported by maritime experts and modern computerized management
                systems, ensuring seamless operational support and full
                regulatory compliance (MLC 2006).
              </p>

              <div className="pt-2">
                <Link
                  to="/services"
                  className="inline-flex items-center gap-3 px-6 py-3.5 bg-[#0A2540] hover:bg-blue-600 text-white font-bold rounded-xl transition-all shadow-md group"
                >
                  <span>EXPLORE OUR SERVICES</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </article>
          </div>
        </section>
      </main>

      <style jsx global>{`
        @import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;700;800;900&display=swap");

        .swiper-pagination-bullet {
          background: #ffffff !important;
          opacity: 0.6;
        }
        .swiper-pagination-bullet-active {
          opacity: 1;
          background: #2563eb !important;
        }
      `}</style>
    </>
  );
};

export default Home;